import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import DashboardHeader from "../components/DashboardHeader";
import MobileBottomNav from "../components/MobileBottomNav";
import FloatingHelpAvatar from "../components/FloatingHelpAvatar";
import QRScannerTab from "../components/QRScannerTab";
import PaeRegistrosDia from "./pae/PaeRegistrosDia";
import MovilidadRegistrosDia from "./movilidad/MovilidadRegistrosDia";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  ClipboardCheck, Users, UserCheck, FileText, Calendar, ChevronRight,
  Loader2, AlertCircle, Check, Clock, X, Save, RefreshCw, Download,
  User, Filter, CheckCircle2, XCircle, AlertTriangle, QrCode, Circle,
  Eye, ChevronLeft, CheckCircle, UtensilsCrossed, MessageSquareText, Bus,
  Wrench, CalendarDays
} from "lucide-react";
import JustificationModal, { JustificationInfoPopover } from "../components/JustificationModal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status configurations
const STUDENT_STATUSES = [
  { id: "pending", label: "Pendiente", icon: Circle, color: "slate", bgColor: "bg-slate-100", textColor: "text-slate-500", borderColor: "border-slate-300" },
  { id: "present", label: "Presente", icon: CheckCircle2, color: "emerald", bgColor: "bg-emerald-100", textColor: "text-emerald-700", borderColor: "border-emerald-500" },
  { id: "late", label: "Tardanza", icon: Clock, color: "amber", bgColor: "bg-amber-100", textColor: "text-amber-700", borderColor: "border-amber-500" },
  { id: "absent", label: "Ausente", icon: XCircle, color: "red", bgColor: "bg-red-100", textColor: "text-red-700", borderColor: "border-red-500" },
  { id: "justified", label: "Justificado", icon: AlertTriangle, color: "amber", bgColor: "bg-amber-100", textColor: "text-amber-700", borderColor: "border-amber-500" }
];

const TEACHER_STATUSES = STUDENT_STATUSES;

// Local storage keys for filter persistence
const STORAGE_KEYS = {
  GRADE: "attendance_last_grade",
  SECTION: "attendance_last_section",
  DATE: "attendance_last_date"
};

// ══════════════════════════════════════════════════════════════════════════════
// STATUS BUTTON COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
function StatusButton({ status, isActive, onClick, disabled }) {
  const Icon = status.icon;
  return (
    <button
      type="button"
      onClick={() => onClick(status.id)}
      disabled={disabled}
      className={`flex flex-col items-center justify-center min-w-[56px] px-2 py-1.5 sm:flex-row sm:gap-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all ${
        isActive
          ? `${status.bgColor} ${status.textColor} border-2 ${status.borderColor} shadow-sm`
          : "bg-slate-100 text-slate-500 border-2 border-transparent hover:bg-slate-200"
      } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <Icon className="w-4 h-4" />
      <span className="text-[10px] mt-0.5 sm:mt-0 sm:text-sm">{status.label}</span>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STUDENT ATTENDANCE TAB
// ══════════════════════════════════════════════════════════════════════════════
function StudentAttendanceTab({ token, schoolId }) {
  const [grades, setGrades] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSavedRecords, setHasSavedRecords] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [markingEntry, setMarkingEntry] = useState(null);
  const [markingExit, setMarkingExit] = useState(null);
  const [justifyModal, setJustifyModal] = useState(null); // { studentId, studentName, existingData }
  const [savingJustification, setSavingJustification] = useState(false);
  const [justificationInfo, setJustificationInfo] = useState(null); // data for popover
  
  const headers = { Authorization: `Bearer ${token}` };

  // Load grades on mount
  useEffect(() => {
    loadGrades();
    // Restore last selection from localStorage
    const lastGrade = localStorage.getItem(STORAGE_KEYS.GRADE);
    const lastSection = localStorage.getItem(STORAGE_KEYS.SECTION);
    const lastDate = localStorage.getItem(STORAGE_KEYS.DATE);
    if (lastGrade) setSelectedGrade(lastGrade);
    if (lastSection) setSelectedSection(lastSection);
    if (lastDate) setSelectedDate(lastDate);
  }, []);

  // Load sections when grade changes
  useEffect(() => {
    if (selectedGrade) {
      loadSections(selectedGrade);
      localStorage.setItem(STORAGE_KEYS.GRADE, selectedGrade);
    }
  }, [selectedGrade]);

  // Save section selection
  useEffect(() => {
    if (selectedSection) {
      localStorage.setItem(STORAGE_KEYS.SECTION, selectedSection);
    }
  }, [selectedSection]);

  // Save date selection
  useEffect(() => {
    if (selectedDate) {
      localStorage.setItem(STORAGE_KEYS.DATE, selectedDate);
    }
  }, [selectedDate]);

  const loadGrades = async () => {
    try {
      const res = await axios.get(`${API}/academic/grades`, { headers });
      // Sort by level order
      const levelOrder = { 'inicial': 1, 'primaria': 2, 'secundaria': 3 };
      const sortedGrades = res.data.filter(g => g.activo).sort((a, b) => {
        const levelA = levelOrder[a.nivel_nombre?.toLowerCase()] || 99;
        const levelB = levelOrder[b.nivel_nombre?.toLowerCase()] || 99;
        if (levelA !== levelB) return levelA - levelB;
        return (a.orden || 0) - (b.orden || 0);
      });
      setGrades(sortedGrades);
    } catch (err) {
      console.error("Error loading grades:", err);
    }
  };

  const loadSections = async (gradeId) => {
    try {
      const res = await axios.get(`${API}/academic/sections`, { headers });
      const gradeSections = res.data.filter(s => s.activo && s.grado_id === gradeId);
      setSections(gradeSections);
      // If current section not in new list, reset
      if (!gradeSections.find(s => s.id === selectedSection)) {
        setSelectedSection("");
      }
    } catch (err) {
      console.error("Error loading sections:", err);
    }
  };

  const loadAttendance = async () => {
    if (!selectedGrade || !selectedSection || !selectedDate) {
      setError("Selecciona grado, sección y fecha");
      return;
    }
    
    setLoading(true);
    setError("");
    setStudents([]);
    
    try {
      const res = await axios.get(`${API}/attendance/students`, {
        headers,
        params: {
          grade_id: selectedGrade,
          section_id: selectedSection,
          date: selectedDate
        }
      });
      // If no saved records, set all students to "pending" status
      const studentsData = res.data.students
        .map(s => ({
          ...s,
          status: res.data.has_saved_records ? s.status : "pending"
        }))
        .sort((a, b) => {
          const aKey = (a.last_name || a.full_name || a.name || "").trim();
          const bKey = (b.last_name || b.full_name || b.name || "").trim();
          const cmp = aKey.localeCompare(bKey, "es", { sensitivity: "base" });
          if (cmp !== 0) return cmp;
          return (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" });
        });
      setStudents(studentsData);
      setHasSavedRecords(res.data.has_saved_records);
      setHasChanges(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar asistencia");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (studentId, newStatus) => {
    if (newStatus === "justified") {
      const student = students.find(s => s.id === studentId);
      setJustifyModal({
        studentId,
        studentName: student?.full_name || "",
        existingData: student?.justification_reason ? {
          justification_reason: student.justification_reason,
          justification_note: student.justification_note,
        } : null
      });
      return;
    }
    setStudents(prev => prev.map(s => 
      s.id === studentId ? { ...s, status: newStatus } : s
    ));
    setHasChanges(true);
  };

  const handleJustifySave = async ({ justification_reason, justification_note }) => {
    if (!justifyModal) return;
    setSavingJustification(true);
    try {
      const res = await axios.post(`${API}/attendance/justify`, {
        student_id: justifyModal.studentId,
        date: selectedDate,
        justification_reason,
        justification_note,
        grade_id: selectedGrade,
        section_id: selectedSection,
      }, { headers });
      setStudents(prev => prev.map(s =>
        s.id === justifyModal.studentId ? {
          ...s,
          status: "justified",
          justification_reason: res.data.justification_reason,
          justification_note: res.data.justification_note,
          justified_by: res.data.justified_by,
          justified_by_name: res.data.justified_by_name,
          justified_at: res.data.justified_at,
        } : s
      ));
      setJustifyModal(null);
      setSuccess("Justificación registrada correctamente");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al justificar");
      setTimeout(() => setError(""), 3000);
    } finally {
      setSavingJustification(false);
    }
  };

  const markEntry = async (studentId) => {
    setMarkingEntry(studentId);
    setError("");
    try {
      const res = await axios.post(`${API}/attendance/mark-entry`, {
        student_id: studentId, date: selectedDate, method: "manual"
      }, { headers });
      setStudents(prev => prev.map(s =>
        s.id === studentId ? { ...s, entry_time: res.data.entry_time, status: "present", entry_method: "manual" } : s
      ));
      setSuccess(`Entrada registrada: ${res.data.entry_time}`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al registrar entrada");
      setTimeout(() => setError(""), 3000);
    } finally {
      setMarkingEntry(null);
    }
  };

  const markExit = async (studentId) => {
    setMarkingExit(studentId);
    setError("");
    try {
      const res = await axios.post(`${API}/attendance/mark-exit`, {
        student_id: studentId, date: selectedDate, method: "manual"
      }, { headers });
      setStudents(prev => prev.map(s =>
        s.id === studentId ? { ...s, exit_time: res.data.exit_time, total_minutes: res.data.total_minutes, exit_method: "manual" } : s
      ));
      setSuccess(`Salida registrada: ${res.data.exit_time}`);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al registrar salida");
      setTimeout(() => setError(""), 3000);
    } finally {
      setMarkingExit(null);
    }
  };

  const handleSaveClick = () => {
    if (students.length === 0) return;
    
    // Check for pending students
    const pendingCount = students.filter(s => s.status === "pending").length;
    if (pendingCount > 0) {
      setShowPendingModal(true);
    } else {
      saveAttendance();
    }
  };

  const saveAttendance = async () => {
    if (students.length === 0) return;
    
    setSaving(true);
    setError("");
    setSuccess("");
    setShowPendingModal(false);
    
    try {
      // Filter out pending students - only save those with actual status
      const records = students
        .filter(s => s.status !== "pending")
        .map(s => ({
          user_id: s.id,
          status: s.status
        }));
      
      await axios.post(`${API}/attendance/students/save`, {
        date: selectedDate,
        grade_id: selectedGrade,
        section_id: selectedSection,
        records
      }, { headers });
      
      setSuccess("Asistencia guardada correctamente");
      setHasChanges(false);
      setHasSavedRecords(true);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al guardar asistencia");
    } finally {
      setSaving(false);
    }
  };

  // Calculate summary
  const summary = {
    pending: students.filter(s => s.status === "pending").length,
    present: students.filter(s => s.status === "present" || s.status === "late").length,
    late: students.filter(s => s.status === "late").length,
    absent: students.filter(s => s.status === "absent").length,
    total: students.length,
    with_entry: students.filter(s => s.entry_time).length,
    with_exit: students.filter(s => s.exit_time).length
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-600" />
          Filtros de Asistencia
        </h3>
        
        <div className="grid md:grid-cols-4 gap-4">
          {/* Grade */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Grado</label>
            <select
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccionar grado</option>
              {grades.map(g => (
                <option key={g.id} value={g.id}>
                  {g.nivel_nombre} - {g.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Section */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Sección</label>
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              disabled={!selectedGrade}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <option value="">Seleccionar sección</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Fecha</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Load button */}
          <div className="flex items-end">
            <button
              onClick={loadAttendance}
              disabled={loading || !selectedGrade || !selectedSection}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
              Cargar
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Attendance Table */}
      {students.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header with summary */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold">Asistencia del {new Date(selectedDate + "T12:00:00").toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long" })}</h3>
                <p className="text-blue-100">{summary.total} estudiantes</p>
              </div>
              <div className="flex gap-2 text-sm flex-wrap">
                <div className="bg-white/20 px-3 py-2 rounded-lg" data-testid="counter-entry">
                  <span className="text-emerald-300">Entrada {summary.with_entry}</span>
                </div>
                <div className="bg-white/20 px-3 py-2 rounded-lg" data-testid="counter-exit">
                  <span className="text-blue-300">Salida {summary.with_exit}</span>
                </div>
                <div className="bg-white/20 px-3 py-2 rounded-lg" data-testid="counter-late">
                  <span className="text-amber-300">Tardanza {summary.late}</span>
                </div>
                <div className="bg-white/20 px-3 py-2 rounded-lg" data-testid="counter-absent">
                  <span className="text-red-300">Ausentes {summary.absent}</span>
                </div>
                {summary.pending > 0 && (
                  <div className="bg-white/20 px-3 py-2 rounded-lg" data-testid="counter-pending">
                    <span className="text-slate-300">Pendientes {summary.pending}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Unsaved changes warning */}
            {hasChanges && (
              <div className="mt-3 p-3 bg-amber-500/30 rounded-lg flex items-center gap-2 text-amber-100">
                <AlertTriangle className="w-5 h-5" />
                <span>Tienes cambios pendientes por guardar</span>
              </div>
            )}
          </div>

          {/* Student list */}
          <div className="divide-y divide-slate-100">
            {students.map((student, idx) => (
              <div
                key={student.id}
                data-testid={`student-row-${student.id}`}
                className={`p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 hover:bg-slate-50 transition-colors ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                }`}
              >
                {/* Avatar + Name row */}
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex-shrink-0">
                    {student.photo_url ? (
                      <img src={student.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-white font-bold text-sm">
                        {student.name?.charAt(0) || "E"}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 md:w-40">
                    <p className="font-semibold text-slate-800 text-sm truncate">{((student.last_name || "") + " " + (student.name || "")).trim() || student.full_name}</p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap items-center gap-2 md:gap-3 md:flex-nowrap md:flex-1">
                  {/* Entry/Exit times */}
                  <div className="flex gap-2 items-center">
                    <div className="flex items-center gap-1.5" data-testid={`entry-${student.id}`}>
                      {student.entry_time ? (
                        <span className="text-xs md:text-sm font-medium text-emerald-600 bg-emerald-50 px-2 md:px-2.5 py-1 rounded-lg">
                          Entrada {student.entry_time}
                        </span>
                      ) : (
                        <button
                          onClick={() => markEntry(student.id)}
                          disabled={markingEntry === student.id || student.status === "absent"}
                          data-testid={`mark-entry-${student.id}`}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {markingEntry === student.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : "Marcar Entrada"}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5" data-testid={`exit-${student.id}`}>
                      {student.exit_time ? (
                        <span className="text-xs md:text-sm font-medium text-blue-600 bg-blue-50 px-2 md:px-2.5 py-1 rounded-lg">
                          Salida {student.exit_time}
                          {student.total_minutes != null && (
                            <span className="text-xs text-blue-400 ml-1">({student.total_minutes}min)</span>
                          )}
                        </span>
                      ) : (
                        <button
                          onClick={() => markExit(student.id)}
                          disabled={!student.entry_time || markingExit === student.id}
                          data-testid={`mark-exit-${student.id}`}
                          className="text-xs font-medium px-2.5 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {markingExit === student.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : "Marcar Salida"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Status buttons */}
                  <div className="flex gap-2 flex-wrap md:flex-nowrap md:ml-auto items-center">
                    {STUDENT_STATUSES.map(status => (
                      <StatusButton
                        key={status.id}
                        status={status}
                        isActive={student.status === status.id}
                        onClick={(newStatus) => handleStatusChange(student.id, newStatus)}
                      />
                    ))}
                    {student.status === "justified" && student.justification_reason && (
                      <button
                        onClick={() => setJustificationInfo(student)}
                        className="w-7 h-7 rounded-full bg-amber-100 text-amber-600 hover:bg-amber-200 flex items-center justify-center transition-colors flex-shrink-0"
                        title="Ver detalle de justificación"
                        data-testid={`justification-info-${student.id}`}
                      >
                        <MessageSquareText className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Save button */}
          <div className="p-6 bg-slate-50 border-t border-slate-200">
            <button
              onClick={handleSaveClick}
              disabled={saving || !hasChanges}
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${
                hasChanges
                  ? "bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed"
              }`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-6 h-6" />
                  {hasSavedRecords ? "Actualizar Asistencia" : "Guardar Asistencia"}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && students.length === 0 && selectedGrade && selectedSection && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Sin estudiantes</h3>
          <p className="text-slate-500">No hay estudiantes registrados en esta sección o no se ha cargado la asistencia.</p>
          <button
            onClick={loadAttendance}
            className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600"
          >
            Cargar asistencia
          </button>
        </div>
      )}

      {/* Pending Confirmation Modal */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95">
            <div className="p-6 text-center">
              {/* Warning Icon */}
              <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
              
              {/* Title */}
              <h3 className="text-xl font-semibold text-slate-800 mb-2">
                Estudiantes sin registrar
              </h3>
              
              {/* Message */}
              <p className="text-slate-500 mb-6">
                Hay <span className="font-bold text-amber-600">{summary.pending} estudiante{summary.pending !== 1 ? 's' : ''}</span> sin registrar asistencia.
                <br />
                ¿Deseas continuar de todas formas?
              </p>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPendingModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveAttendance}
                  className="flex-1 px-4 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors"
                >
                  Guardar de todos modos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Justification Modal */}
      <JustificationModal
        isOpen={!!justifyModal}
        onClose={() => setJustifyModal(null)}
        onSave={handleJustifySave}
        studentName={justifyModal?.studentName || ""}
        existingData={justifyModal?.existingData}
        saving={savingJustification}
      />

      {/* Justification Info Popover */}
      {justificationInfo && (
        <JustificationInfoPopover
          data={justificationInfo}
          onClose={() => setJustificationInfo(null)}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEACHER ATTENDANCE TAB
// ══════════════════════════════════════════════════════════════════════════════
function TeacherAttendanceTab({ token, schoolId }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSavedRecords, setHasSavedRecords] = useState(false);
  const [perLevelActive, setPerLevelActive] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  const headers = { Authorization: `Bearer ${token}` };

  const loadAttendance = async () => {
    setLoading(true);
    setError("");
    
    try {
      const res = await axios.get(`${API}/attendance/teachers`, {
        headers,
        params: { date: selectedDate, include_schedule: true }
      });
      const teachersSorted = (res.data.teachers || []).slice().sort((a, b) => {
        const aKey = (a.last_name || a.full_name || a.name || "").trim();
        const bKey = (b.last_name || b.full_name || b.name || "").trim();
        const cmp = aKey.localeCompare(bKey, "es", { sensitivity: "base" });
        if (cmp !== 0) return cmp;
        return (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" });
      });
      setTeachers(teachersSorted);
      setHasSavedRecords(res.data.has_saved_records);
      setPerLevelActive(!!res.data.per_level_schedule_active);
      setHasChanges(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar asistencia");
    } finally {
      setLoading(false);
    }
  };

  // Load on date change
  useEffect(() => {
    loadAttendance();
  }, [selectedDate]);

  const handleStatusChange = (teacherId, newStatus) => {
    setTeachers(prev => prev.map(t => 
      t.id === teacherId ? { ...t, status: newStatus } : t
    ));
    setHasChanges(true);
  };

  const saveAttendance = async () => {
    if (teachers.length === 0) return;
    
    setSaving(true);
    setError("");
    setSuccess("");
    
    try {
      // Only send teachers with a real status — "pending" is not a valid
      // backend status (it means "no record yet") and would trigger a 422.
      const records = teachers
        .filter(t => t.status && t.status !== "pending")
        .map(t => ({
          user_id: t.id,
          status: t.status
        }));
      
      await axios.post(`${API}/attendance/teachers/save`, {
        date: selectedDate,
        records
      }, { headers });
      
      setSuccess("Asistencia de profesores guardada correctamente");
      setHasChanges(false);
      setHasSavedRecords(true);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al guardar asistencia");
    } finally {
      setSaving(false);
    }
  };

  // Calculate summary
  const summary = {
    present: teachers.filter(t => t.status === "present").length,
    late: teachers.filter(t => t.status === "late").length,
    absent: teachers.filter(t => t.status === "absent").length,
    justified: teachers.filter(t => t.status === "justified").length,
    total: teachers.length
  };

  return (
    <div className="space-y-6">
      {/* Date filter */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-indigo-600" />
            <span className="font-semibold text-slate-700">Fecha:</span>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={loadAttendance}
            disabled={loading}
            className="px-6 py-3 bg-indigo-500 text-white rounded-xl font-semibold hover:bg-indigo-600 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            Actualizar
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
          <Check className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Teacher list */}
      {teachers.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold">Asistencia del {new Date(selectedDate + "T12:00:00").toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long" })}</h3>
                <p className="text-indigo-100">{summary.total} profesores</p>
              </div>
              <div className="flex gap-3 text-sm flex-wrap">
                <div className="bg-white/20 px-3 py-2 rounded-lg">
                  <span className="text-emerald-300">✓ {summary.present}</span>
                </div>
                <div className="bg-white/20 px-3 py-2 rounded-lg">
                  <span className="text-amber-300">⏰ {summary.late}</span>
                </div>
                <div className="bg-white/20 px-3 py-2 rounded-lg">
                  <span className="text-red-300">✗ {summary.absent}</span>
                </div>
                <div className="bg-white/20 px-3 py-2 rounded-lg">
                  <span className="text-blue-300">📝 {summary.justified}</span>
                </div>
              </div>
            </div>
            
            {hasChanges && (
              <div className="mt-3 p-3 bg-amber-500/30 rounded-lg flex items-center gap-2 text-amber-100">
                <AlertTriangle className="w-5 h-5" />
                <span>Tienes cambios pendientes por guardar</span>
              </div>
            )}
          </div>

          {/* Teacher list */}
          <div className="divide-y divide-slate-100">
            {teachers.map((teacher, idx) => (
              <div
                key={teacher.id}
                className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${
                  idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                }`}
              >
                {/* Avatar */}
                <div className="flex-shrink-0">
                  {teacher.photo_url ? (
                    <img src={teacher.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">
                      {teacher.name?.charAt(0) || "P"}
                    </div>
                  )}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{((teacher.last_name || "") + " " + (teacher.name || "")).trim() || teacher.full_name}</p>
                  <p className="text-sm text-slate-500">{teacher.email}</p>
                  {perLevelActive && teacher.effective_schedule && (
                    <span
                      className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold border border-slate-200"
                      data-testid={`teacher-schedule-badge-${teacher.id}`}
                      title={teacher.effective_schedule.source === "level"
                        ? `Horario del nivel ${teacher.effective_schedule.nivel_name || ""}`
                        : "Sin nivel asignado · usa horario general"}
                    >
                      <Clock className="w-3 h-3" />
                      {teacher.effective_schedule.nivel_name
                        ? `${teacher.effective_schedule.nivel_name} · `
                        : "General · "}
                      {teacher.effective_schedule.entry_time} – {teacher.effective_schedule.exit_time}
                    </span>
                  )}
                </div>

                {/* Status buttons */}
                <div className="flex gap-2 flex-wrap justify-end">
                  {TEACHER_STATUSES.map(status => (
                    <StatusButton
                      key={status.id}
                      status={status}
                      isActive={teacher.status === status.id}
                      onClick={(newStatus) => handleStatusChange(teacher.id, newStatus)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Save button */}
          <div className="p-6 bg-slate-50 border-t border-slate-200">
            <button
              onClick={saveAttendance}
              disabled={saving || !hasChanges}
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${
                hasChanges
                  ? "bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed"
              }`}
            >
              {saving ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-6 h-6" />
                  {hasSavedRecords ? "Actualizar Asistencia" : "Guardar Asistencia"}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && teachers.length === 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <UserCheck className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Sin profesores</h3>
          <p className="text-slate-500">No hay profesores registrados en el sistema.</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TEACHER MONTHLY REPORT (premium)
// ══════════════════════════════════════════════════════════════════════════════
const MONTH_NAMES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const STATUS_DOT = {
  present: "bg-emerald-500",
  late: "bg-amber-500",
  absent: "bg-rose-500",
  justified: "bg-sky-500",
  pending: "bg-slate-200",
};

// ── Estados unificados para el reporte diario de profesores (fusión premium) ──
const ESTADO_STYLES = {
  "Completo": { bg: "#E1F5EE", text: "#0F6E56" },
  "Presente": { bg: "#E1F5EE", text: "#0F6E56" },
  "Tardanza": { bg: "#FAEEDA", text: "#854F0B" },
  "Salida anticipada": { bg: "#FAEEDA", text: "#854F0B" },
  "Ausente": { bg: "#FCEBEB", text: "#A32D2D" },
  "Pendiente": { bg: "#E6F1FB", text: "#185FA5" },
  "Justificado": { bg: "#E6F1FB", text: "#185FA5" },
};
const WARNING_COLOR = "#854F0B"; // resalta entrada con tardanza / salida anticipada
const formatMinutes = (min) => {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
};

// ── Vista diaria premium reutilizable (profesores / alumnos / administrativo) ──
function PremiumDailyAttendance({ data, nameHeader = "Nombre", okLabel = "Completos", title = "Asistencia", pdfColor = [124, 58, 237] }) {
  if (!data) return null;
  const rows = data.registros || data.profesores || [];
  const resumen = data.resumen || { total: 0, completos: 0, tardanzas: 0, ausentes: 0, justificados: 0 };
  const fechaLarga = new Date(data.fecha + "T12:00:00").toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long" });

  const exportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFontSize(18);
    doc.setTextColor(pdfColor[0], pdfColor[1], pdfColor[2]);
    doc.text(title, pageWidth / 2, 20, { align: "center" });
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    doc.text(`Fecha: ${new Date(data.fecha + "T12:00:00").toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}`, pageWidth / 2, 28, { align: "center" });
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total: ${resumen.total}   |   ${okLabel}: ${resumen.completos}   |   Tardanzas: ${resumen.tardanzas}   |   Ausentes: ${resumen.ausentes}   |   Justificados: ${resumen.justificados}`, pageWidth / 2, 36, { align: "center" });
    const tableData = rows.map((p) => [
      p.nombre, p.tipo || "—",
      `${p.horario_inicio || "—"} - ${p.horario_fin || "—"}`,
      p.entrada || "—", p.salida || "—", formatMinutes(p.minutos_trabajados), p.estado,
    ]);
    autoTable(doc, {
      startY: 44,
      head: [[nameHeader, "Tipo", "Horario", "Entrada", "Salida", "Horas", "Estado"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: pdfColor, textColor: 255, fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 42 }, 2: { halign: "center" }, 3: { halign: "center" }, 4: { halign: "center" }, 5: { halign: "center" }, 6: { halign: "center" } },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });
    doc.save(`${title.toLowerCase().replace(/\s+/g, "_")}_${data.fecha}.pdf`);
  };

  const cards = [
    { key: "total", label: "Total", value: resumen.total, color: "#475569", bg: "#F1F5F9" },
    { key: "completos", label: okLabel, value: resumen.completos, color: ESTADO_STYLES["Completo"].text, bg: ESTADO_STYLES["Completo"].bg },
    { key: "tardanzas", label: "Tardanzas", value: resumen.tardanzas, color: ESTADO_STYLES["Tardanza"].text, bg: ESTADO_STYLES["Tardanza"].bg },
    { key: "ausentes", label: "Ausentes", value: resumen.ausentes, color: ESTADO_STYLES["Ausente"].text, bg: ESTADO_STYLES["Ausente"].bg },
    { key: "justificados", label: "Justificados", value: resumen.justificados, color: ESTADO_STYLES["Justificado"].text, bg: ESTADO_STYLES["Justificado"].bg },
  ];

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="daily-summary">
        {cards.map((c) => (
          <div key={c.key} className="rounded-2xl p-4 text-center border" style={{ backgroundColor: c.bg, borderColor: "rgba(0,0,0,0.06)" }} data-testid={`daily-card-${c.key}`}>
            <p className="text-2xl font-medium" style={{ color: c.color }}>{c.value}</p>
            <p className="text-xs font-medium mt-0.5" style={{ color: c.color, opacity: 0.85 }}>{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-100">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-bold text-slate-800 flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-violet-600" />
            Asistencia del {fechaLarga}
            <span className="text-slate-400 font-medium">({rows.length})</span>
          </h3>
          <button onClick={exportPDF} className="px-4 py-2 bg-violet-100 text-violet-700 rounded-xl text-sm font-semibold hover:bg-violet-200 transition-colors flex items-center gap-2" data-testid="daily-export-pdf">
            <Download className="w-4 h-4" /> Exportar PDF
          </button>
        </div>

        {rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No hay registros para esta fecha.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">{nameHeader}</th>
                  <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Tipo</th>
                  <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Horario</th>
                  <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Entrada</th>
                  <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Salida</th>
                  <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Horas</th>
                  <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide">Estado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => {
                  const st = ESTADO_STYLES[p.estado] || { bg: "#F1F5F9", text: "#475569" };
                  const isLate = p.estado === "Tardanza";
                  const isEarlyExit = p.estado === "Salida anticipada";
                  return (
                    <tr key={p.id} className="hover:bg-slate-50/70 transition-colors" data-testid={`daily-row-${p.id}`}>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          {p.photo_url ? (
                            <img src={p.photo_url} alt="" className="w-[30px] h-[30px] rounded-full object-cover" />
                          ) : (
                            <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center text-[11px] font-bold" style={{ backgroundColor: st.bg, color: st.text }}>{p.iniciales}</div>
                          )}
                          <span className="font-medium text-slate-800 text-sm">{p.nombre}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-500">{p.tipo}</td>
                      <td className="text-center px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{p.horario_inicio || "—"} <span className="text-slate-300">–</span> {p.horario_fin || "—"}</td>
                      <td className="text-center px-3 py-3 text-sm font-semibold whitespace-nowrap" style={{ color: p.entrada ? (isLate ? WARNING_COLOR : "#0f172a") : "#cbd5e1" }}>{p.entrada || "—"}</td>
                      <td className="text-center px-3 py-3 text-sm font-semibold whitespace-nowrap" style={{ color: p.salida ? (isEarlyExit ? WARNING_COLOR : "#0f172a") : "#cbd5e1" }}>{p.salida || "—"}</td>
                      <td className="text-center px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{formatMinutes(p.minutos_trabajados)}</td>
                      <td className="text-center px-3 py-3"><span className="inline-block text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: st.bg, color: st.text }}>{p.estado}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function TeacherMonthlyReport({ token }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/attendance/teachers/monthly`, { headers, params: { month, year } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar el reporte mensual");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year]);

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const totals = data?.totals || { present: 0, late: 0, absent: 0, justified: 0 };
  const grandTotal = totals.present + totals.late + totals.absent + totals.justified;

  return (
    <div className="space-y-6">
      {/* Month navigator */}
      <div className="bg-white rounded-2xl shadow-lg p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="monthly-prev">
            <ChevronLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="px-4 text-center min-w-[180px]">
            <p className="text-lg font-bold text-slate-800">{MONTH_NAMES[month - 1]} {year}</p>
            <p className="text-xs text-slate-400">{data?.total_teachers || 0} profesores</p>
          </div>
          <button onClick={nextMonth} className="w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors" data-testid="monthly-next">
            <ChevronRight className="w-5 h-5 text-slate-600" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400" data-testid="monthly-month-select">
            {MONTH_NAMES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400" data-testid="monthly-year-select">
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />{error}
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Asistencias", value: totals.present, color: "from-emerald-500 to-teal-600", icon: Check },
          { label: "Tardanzas", value: totals.late, color: "from-amber-500 to-orange-500", icon: Clock },
          { label: "Faltas", value: totals.absent, color: "from-rose-500 to-red-600", icon: AlertCircle },
          { label: "Justificadas", value: totals.justified, color: "from-sky-500 to-blue-600", icon: UserCheck },
        ].map((c) => {
          const Icon = c.icon;
          const pct = grandTotal ? Math.round((c.value / grandTotal) * 100) : 0;
          return (
            <div key={c.label} className={`rounded-2xl p-5 text-white bg-gradient-to-br ${c.color} shadow-lg`} data-testid={`monthly-summary-${c.label}`}>
              <div className="flex items-center justify-between">
                <Icon className="w-6 h-6 opacity-90" />
                <span className="text-xs font-semibold bg-white/25 px-2 py-0.5 rounded-full">{pct}%</span>
              </div>
              <p className="text-3xl font-bold mt-3">{c.value}</p>
              <p className="text-sm opacity-90">{c.label}</p>
            </div>
          );
        })}
      </div>

      {/* Per-teacher table */}
      {loading ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-500" />
        </div>
      ) : (data?.teachers || []).length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <CalendarDays className="w-14 h-14 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500">No hay registros de asistencia este mes.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
            <span className="font-semibold text-slate-700">Detalle por profesor</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Presente</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Tardanza</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-rose-500" /> Falta</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-sky-500" /> Justificada</span>
          </div>
          <div className="divide-y divide-slate-100">
            {data.teachers.map((t, idx) => (
              <div key={t.id} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50/60 transition-colors" data-testid={`monthly-teacher-row-${idx}`}>
                <div className="flex-shrink-0">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt="" className="w-11 h-11 rounded-full object-cover" />
                  ) : (
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">
                      {(t.last_name || t.name || "P").charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800 truncate">{((t.last_name || "") + " " + (t.name || "")).trim() || t.full_name}</p>
                  {/* Mini calendar heatmap */}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {Array.from({ length: data.days_in_month }, (_, i) => {
                      const dstr = `${year}-${String(month).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`;
                      const st = t.days?.[dstr] || "pending";
                      return <span key={i} title={`${i + 1}: ${st}`} className={`w-2.5 h-2.5 rounded-sm ${STATUS_DOT[st]}`} />;
                    })}
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-3 text-sm flex-shrink-0">
                  <span className="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 font-semibold" title="Presente">{t.counts.present}</span>
                  <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-700 font-semibold" title="Tardanza">{t.counts.late}</span>
                  <span className="px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 font-semibold" title="Falta">{t.counts.absent}</span>
                  <span className="px-2.5 py-1 rounded-lg bg-sky-50 text-sky-700 font-semibold" title="Justificada">{t.counts.justified}</span>
                </div>
                <div className="flex-shrink-0 w-16 text-right">
                  <p className={`text-lg font-bold ${t.attendance_rate >= 90 ? "text-emerald-600" : t.attendance_rate >= 75 ? "text-amber-600" : "text-rose-600"}`}>{t.attendance_rate}%</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide">asistencia</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAINTENANCE ATTENDANCE TAB
// ══════════════════════════════════════════════════════════════════════════════
function MaintenanceAttendanceTab({ token }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasSavedRecords, setHasSavedRecords] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const loadAttendance = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/attendance/maintenance`, {
        headers,
        params: { date: selectedDate },
      });
      const peopleSorted = (res.data.maintenance || []).slice().sort((a, b) => {
        const aKey = (a.last_name || a.full_name || a.name || "").trim();
        const bKey = (b.last_name || b.full_name || b.name || "").trim();
        const cmp = aKey.localeCompare(bKey, "es", { sensitivity: "base" });
        if (cmp !== 0) return cmp;
        return (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" });
      });
      setPeople(peopleSorted);
      setHasSavedRecords(res.data.has_saved_records);
      setHasChanges(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Error al cargar asistencia");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  const handleStatusChange = (userId, newStatus) => {
    setPeople(prev => prev.map(p => p.id === userId ? { ...p, status: newStatus } : p));
    setHasChanges(true);
  };

  const saveAttendance = async () => {
    if (people.length === 0) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const records = people
        .filter(p => p.status && p.status !== "pending")
        .map(p => ({ user_id: p.id, status: p.status }));
      await axios.post(`${API}/attendance/maintenance/save`, {
        date: selectedDate,
        records,
      }, { headers });
      setSuccess("Asistencia administrativa guardada correctamente");
      setHasChanges(false);
      setHasSavedRecords(true);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Error al guardar asistencia");
    } finally {
      setSaving(false);
    }
  };

  const summary = {
    present: people.filter(p => p.status === "present").length,
    late: people.filter(p => p.status === "late").length,
    absent: people.filter(p => p.status === "absent").length,
    justified: people.filter(p => p.status === "justified").length,
    total: people.length,
  };

  return (
    <div className="space-y-6" data-testid="maintenance-attendance-tab">
      {/* Date filter */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-amber-600" />
            <span className="font-semibold text-slate-700">Fecha:</span>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="maintenance-attendance-date"
          />
          <button
            onClick={loadAttendance}
            disabled={loading}
            className="px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2"
            data-testid="maintenance-attendance-refresh"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
            Actualizar
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      )}
      {success && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
          <Check className="w-5 h-5" /> {success}
        </div>
      )}

      {people.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold">Asistencia del {new Date(selectedDate + "T12:00:00").toLocaleDateString("es-PE", { weekday: "long", day: "numeric", month: "long" })}</h3>
                <p className="text-amber-100">{summary.total} personas administrativas</p>
              </div>
              <div className="flex gap-3 text-sm flex-wrap">
                <div className="bg-white/20 px-3 py-2 rounded-lg"><span className="text-emerald-100">✓ {summary.present}</span></div>
                <div className="bg-white/20 px-3 py-2 rounded-lg"><span className="text-amber-100">⏰ {summary.late}</span></div>
                <div className="bg-white/20 px-3 py-2 rounded-lg"><span className="text-red-100">✗ {summary.absent}</span></div>
                <div className="bg-white/20 px-3 py-2 rounded-lg"><span className="text-blue-100">📝 {summary.justified}</span></div>
              </div>
            </div>
            {hasChanges && (
              <div className="mt-3 p-3 bg-white/20 rounded-lg flex items-center gap-2 text-amber-50">
                <AlertTriangle className="w-5 h-5" />
                <span>Tienes cambios pendientes por guardar</span>
              </div>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            {people.map((person, idx) => (
              <div key={person.id}
                className={`p-4 flex items-center gap-4 hover:bg-slate-50 transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}`}
                data-testid={`maintenance-row-${person.id}`}
              >
                <div className="flex-shrink-0">
                  {person.photo_url ? (
                    <img src={person.photo_url} alt="" className="w-12 h-12 rounded-full object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                      {person.name?.charAt(0) || "M"}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-800">{((person.last_name || "") + " " + (person.name || "")).trim() || person.full_name}</p>
                  {person.role_label ? (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold border border-amber-200">
                      <Wrench className="w-3 h-3" />
                      {person.role_label}
                    </span>
                  ) : (
                    <p className="text-xs text-slate-400 mt-1">Sin rol asignado</p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {TEACHER_STATUSES.map(status => (
                    <StatusButton
                      key={status.id}
                      status={status}
                      isActive={person.status === status.id}
                      onClick={(newStatus) => handleStatusChange(person.id, newStatus)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="p-6 bg-slate-50 border-t border-slate-200">
            <button
              onClick={saveAttendance}
              disabled={saving || !hasChanges}
              data-testid="maintenance-attendance-save"
              className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${
                hasChanges
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed"
              }`}
            >
              {saving ? (<><Loader2 className="w-6 h-6 animate-spin" />Guardando...</>) :
                (<><Save className="w-6 h-6" />{hasSavedRecords ? "Actualizar Asistencia" : "Guardar Asistencia"}</>)}
            </button>
          </div>
        </div>
      )}

      {!loading && people.length === 0 && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Wrench className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Sin personal administrativo</h3>
          <p className="text-slate-500">No hay personal administrativo registrado en el sistema.</p>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// REPORTS TAB
// ══════════════════════════════════════════════════════════════════════════════
function ReportsTab({ token, schoolId }) {
  const [grades, setGrades] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(true);
  // Vista diaria premium
  const [studentMode, setStudentMode] = useState("day"); // "day" | "range"
  const [dayDate, setDayDate] = useState(new Date().toISOString().split("T")[0]);
  const [dayReport, setDayReport] = useState(null);
  const [detailModal, setDetailModal] = useState(null); // { studentId, studentName, studentPhoto }
  const [detailRecords, setDetailRecords] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMonth, setDetailMonth] = useState(new Date());
  // Monthly planilla PDF (own tab)
  const now = new Date();
  const [reportsSubtab, setReportsSubtab] = useState("reporte"); // "reporte" | "planilla"
  const [monthlyGrade, setMonthlyGrade] = useState("");
  const [monthlySection, setMonthlySection] = useState("");
  const [monthlyMonth, setMonthlyMonth] = useState(now.getMonth() + 1);
  const [monthlyYear, setMonthlyYear] = useState(now.getFullYear());
  const [downloadingMonthly, setDownloadingMonthly] = useState(false);
  const [monthlySections, setMonthlySections] = useState([]);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Load grades on mount
  useEffect(() => {
    loadGrades();
  }, []);

  const loadGrades = async () => {
    try {
      const res = await axios.get(`${API}/academic/grades`, { headers });
      setGrades(res.data || []);
    } catch (err) {
      console.error("Error loading grades:", err);
    } finally {
      setLoadingGrades(false);
    }
  };

  // Load sections when grade changes
  useEffect(() => {
    if (selectedGrade) {
      loadSections(selectedGrade);
    } else {
      setSections([]);
      setSelectedSection("");
    }
  }, [selectedGrade]);

  const loadSections = async (gradeId) => {
    try {
      const res = await axios.get(`${API}/academic/sections?grado_id=${gradeId}`, { headers });
      setSections(res.data || []);
    } catch (err) {
      console.error("Error loading sections:", err);
    }
  };

  const loadReport = async () => {
    if (!selectedGrade || !selectedSection) {
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        grade_id: selectedGrade,
        section_id: selectedSection,
        start_date: startDate,
        end_date: endDate
      });
      
      const res = await axios.get(`${API}/attendance/reports/students?${params}`, { headers });
      setReport(res.data);
    } catch (err) {
      console.error("Error loading report:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadDayReport = async () => {
    if (!selectedGrade || !selectedSection) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/asistencia/alumnos`, {
        headers,
        params: { fecha: dayDate, grade_id: selectedGrade, section_id: selectedSection },
      });
      setDayReport(res.data);
    } catch (err) {
      console.error("Error loading student daily report:", err);
      setDayReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Get grade and section names for display
  const gradeName = grades.find(g => g.id === selectedGrade)?.nombre || "";
  const sectionName = sections.find(s => s.id === selectedSection)?.nombre || "";

  // Student detail modal functions
  const openDetailModal = async (studentId, studentName, studentPhoto) => {
    setDetailModal({ studentId, studentName, studentPhoto });
    setDetailMonth(new Date());
    await loadDetailRecords(studentId, new Date());
  };

  const loadDetailRecords = async (studentId, month) => {
    setDetailLoading(true);
    try {
      const y = month.getFullYear();
      const m = month.getMonth();
      const sd = new Date(y, m, 1).toISOString().split("T")[0];
      const ed = new Date(y, m + 1, 0).toISOString().split("T")[0];
      const res = await axios.get(`${API}/attendance/reports/student-detail?student_id=${studentId}&start_date=${sd}&end_date=${ed}`, { headers });
      setDetailRecords(res.data.records || []);
    } catch (err) {
      console.error("Error:", err);
      setDetailRecords([]);
    } finally {
      setDetailLoading(false);
    }
  };

  const changeDetailMonth = (dir) => {
    const newMonth = new Date(detailMonth.getFullYear(), detailMonth.getMonth() + dir, 1);
    if (newMonth <= new Date()) {
      setDetailMonth(newMonth);
      if (detailModal) loadDetailRecords(detailModal.studentId, newMonth);
    }
  };

  // Individual student PDF report (mirrors the modal calendar view)
  const exportIndividualPDF = () => {
    if (!detailModal) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(18);
    doc.setTextColor(88, 28, 135);
    doc.text("Reporte Individual de Asistencia", pageWidth / 2, 20, { align: "center" });

    // Student name
    doc.setFontSize(13);
    doc.setTextColor(0, 0, 0);
    doc.text(detailModal.studentName || "", pageWidth / 2, 30, { align: "center" });

    // Month label
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const monthLabelRaw = detailMonth.toLocaleDateString("es-PE", { month: "long", year: "numeric" });
    const monthLabel = monthLabelRaw.charAt(0).toUpperCase() + monthLabelRaw.slice(1);
    doc.text(monthLabel, pageWidth / 2, 38, { align: "center" });

    // Stats
    const st = { present: 0, absent: 0, late: 0, justified: 0, total: detailRecords.length };
    detailRecords.forEach(a => { if (st[a.status] !== undefined) st[a.status]++; });
    const pct = st.total > 0 ? Math.round(((st.present + st.justified) / st.total) * 100) : 0;

    // Summary boxes (5 cards)
    const summaryY = 48;
    const boxWidth = 34;
    const boxHeight = 18;
    const startX = 14;
    const drawBox = (idx, fillColor, textColor, value, label) => {
      const x = startX + (boxWidth + 4) * idx;
      doc.setFillColor(...fillColor);
      doc.roundedRect(x, summaryY, boxWidth, boxHeight, 2, 2, "F");
      doc.setFontSize(13);
      doc.setTextColor(...textColor);
      doc.text(String(value), x + boxWidth / 2, summaryY + 8, { align: "center" });
      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(label, x + boxWidth / 2, summaryY + 14, { align: "center" });
    };
    drawBox(0, [16, 185, 129], [255, 255, 255], `${pct}%`, "Asistencia");
    drawBox(1, [209, 250, 229], [4, 120, 87], st.present, "Asistencias");
    drawBox(2, [254, 243, 199], [180, 83, 9], st.late, "Tardanzas");
    drawBox(3, [254, 226, 226], [185, 28, 28], st.absent, "Faltas");
    drawBox(4, [219, 234, 254], [29, 78, 216], st.justified, "Justificadas");

    // Daily detail table
    const monthStart = new Date(detailMonth.getFullYear(), detailMonth.getMonth(), 1);
    const monthEnd = new Date(detailMonth.getFullYear(), detailMonth.getMonth() + 1, 0);
    const dayNames = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
    const statusLabel = {
      present: "Presente",
      late: "Tardanza",
      absent: "Falta",
      justified: "Justificado",
    };
    const formatTime = (iso) => {
      if (!iso) return "—";
      try {
        return new Date(iso).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" });
      } catch { return "—"; }
    };
    const tableData = [];
    for (let d = new Date(monthStart); d <= monthEnd; d.setDate(d.getDate() + 1)) {
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const rec = detailRecords.find(a => a.date === dateStr);
      const isWeekend = [0, 6].includes(d.getDay());
      tableData.push([
        d.getDate(),
        dayNames[d.getDay()],
        rec ? (statusLabel[rec.status] || rec.status) : (isWeekend ? "—" : "Sin registro"),
        rec ? formatTime(rec.entry_time) : "—",
        rec ? formatTime(rec.exit_time) : "—",
        (rec && rec.justification_reason) ? rec.justification_reason : "",
      ]);
    }
    autoTable(doc, {
      startY: summaryY + boxHeight + 8,
      head: [["Día", "Semana", "Estado", "Entrada", "Salida", "Observación"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [124, 58, 237], textColor: 255, fontSize: 9, fontStyle: "bold" },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 14, halign: "center" },
        1: { cellWidth: 26 },
        2: { cellWidth: 28 },
        3: { cellWidth: 24, halign: "center" },
        4: { cellWidth: 24, halign: "center" },
        5: { cellWidth: "auto" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 2) {
          const v = String(data.cell.raw || "");
          if (v === "Presente") data.cell.styles.textColor = [4, 120, 87];
          else if (v === "Tardanza") data.cell.styles.textColor = [180, 83, 9];
          else if (v === "Falta") data.cell.styles.textColor = [185, 28, 28];
          else if (v === "Justificado") data.cell.styles.textColor = [29, 78, 216];
        }
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.text(
        `Generado el ${new Date().toLocaleDateString("es-PE")} - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
    }

    const safeName = (detailModal.studentName || "alumno").replace(/\s+/g, "_");
    const safeMonth = `${detailMonth.getFullYear()}-${String(detailMonth.getMonth() + 1).padStart(2, "0")}`;
    doc.save(`reporte_individual_${safeName}_${safeMonth}.pdf`);
  };

  // Download monthly attendance sheet (PDF generated by backend)
  const downloadMonthlySheet = async () => {
    if (!monthlyGrade || !monthlySection) {
      toast.error("Selecciona Grado y Sección primero");
      return;
    }
    setDownloadingMonthly(true);
    try {
      const params = new URLSearchParams({
        month: String(monthlyMonth),
        year: String(monthlyYear),
        grade_id: monthlyGrade,
        section_id: monthlySection,
      });
      const res = await axios.get(`${API}/attendance/reports/monthly-pdf?${params}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const monthName = [
        "Enero","Febrero","Marzo","Abril","Mayo","Junio",
        "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
      ][monthlyMonth - 1];
      const mGradeName = grades.find(g => g.id === monthlyGrade)?.nombre || "grado";
      const mSectionName = monthlySections.find(s => s.id === monthlySection)?.nombre || "seccion";
      const safeGrade = mGradeName.replace(/\s+/g, "_");
      const safeSection = mSectionName.replace(/\s+/g, "_");
      link.download = `planilla_asistencia_${safeGrade}_${safeSection}_${monthName}_${monthlyYear}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Planilla descargada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al descargar la planilla");
    } finally {
      setDownloadingMonthly(false);
    }
  };

  // Load sections for the monthly planilla grade select
  useEffect(() => {
    if (!monthlyGrade) {
      setMonthlySections([]);
      setMonthlySection("");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API}/academic/sections?grado_id=${monthlyGrade}`, { headers });
        setMonthlySections(res.data || []);
      } catch {
        setMonthlySections([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlyGrade]);

  // Export to PDF function
  const exportToPDF = () => {
    if (!report) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();    
    // Title
    doc.setFontSize(18);
    doc.setTextColor(88, 28, 135); // Purple color
    doc.text("Reporte de Asistencia de Estudiantes", pageWidth / 2, 20, { align: "center" });
    
    // Subtitle with grade, section and dates
    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const subtitle = `${gradeName} - Sección ${sectionName}`;
    const dateRange = `Período: ${new Date(startDate + 'T12:00:00').toLocaleDateString("es-PE")} - ${new Date(endDate + 'T12:00:00').toLocaleDateString("es-PE")}`;
    doc.text(subtitle, pageWidth / 2, 28, { align: "center" });
    doc.text(dateRange, pageWidth / 2, 34, { align: "center" });
    
    // Summary section
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text("Resumen General", 14, 48);
    
    // Summary boxes
    const summaryY = 54;
    const boxWidth = 42;
    const boxHeight = 18;
    const startX = 14;
    
    // Total
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(startX, summaryY, boxWidth, boxHeight, 2, 2, 'F');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(String(report.summary.total_records), startX + boxWidth/2, summaryY + 8, { align: "center" });
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("Total registros", startX + boxWidth/2, summaryY + 14, { align: "center" });
    
    // Asistencias
    doc.setFillColor(209, 250, 229);
    doc.roundedRect(startX + boxWidth + 4, summaryY, boxWidth, boxHeight, 2, 2, 'F');
    doc.setFontSize(14);
    doc.setTextColor(4, 120, 87);
    doc.text(String(report.summary.present), startX + boxWidth + 4 + boxWidth/2, summaryY + 8, { align: "center" });
    doc.setFontSize(8);
    doc.text("Asistencias", startX + boxWidth + 4 + boxWidth/2, summaryY + 14, { align: "center" });
    
    // Tardanzas
    doc.setFillColor(254, 243, 199);
    doc.roundedRect(startX + (boxWidth + 4) * 2, summaryY, boxWidth, boxHeight, 2, 2, 'F');
    doc.setFontSize(14);
    doc.setTextColor(180, 83, 9);
    doc.text(String(report.summary.late), startX + (boxWidth + 4) * 2 + boxWidth/2, summaryY + 8, { align: "center" });
    doc.setFontSize(8);
    doc.text("Tardanzas", startX + (boxWidth + 4) * 2 + boxWidth/2, summaryY + 14, { align: "center" });
    
    // Inasistencias
    doc.setFillColor(254, 226, 226);
    doc.roundedRect(startX + (boxWidth + 4) * 3, summaryY, boxWidth, boxHeight, 2, 2, 'F');
    doc.setFontSize(14);
    doc.setTextColor(185, 28, 28);
    doc.text(String(report.summary.absent), startX + (boxWidth + 4) * 3 + boxWidth/2, summaryY + 8, { align: "center" });
    doc.setFontSize(8);
    doc.text("Inasistencias", startX + (boxWidth + 4) * 3 + boxWidth/2, summaryY + 14, { align: "center" });
    
    // Table
    if (report.report.length > 0) {
      const tableData = report.report.map(item => [
        item.student_name,
        item.total_days,
        item.present,
        item.late,
        item.absent,
        `${item.attendance_rate}%`
      ]);
      
      autoTable(doc, {
        startY: 80,
        head: [['Estudiante', 'Días', 'Asistencias', 'Tardanzas', 'Inasistencias', '% Asistencia']],
        body: tableData,
        theme: 'striped',
        headStyles: { 
          fillColor: [124, 58, 237],
          textColor: 255,
          fontSize: 10,
          fontStyle: 'bold'
        },
        bodyStyles: {
          fontSize: 9
        },
        columnStyles: {
          0: { cellWidth: 60 },
          1: { cellWidth: 20, halign: 'center' },
          2: { cellWidth: 25, halign: 'center' },
          3: { cellWidth: 25, halign: 'center' },
          4: { cellWidth: 28, halign: 'center' },
          5: { cellWidth: 28, halign: 'center' }
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252]
        }
      });
    }
    
    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.text(
        `Generado el ${new Date().toLocaleDateString("es-PE")} - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );
    }
    
    // Save
    const fileName = `reporte_asistencia_${gradeName.replace(/\s+/g, '_')}_${sectionName}_${startDate}_${endDate}.pdf`;
    doc.save(fileName);
  };

  // Export to Excel function (mismo contenido que el PDF)
  const exportToExcel = () => {
    if (!report) return;

    const wb = XLSX.utils.book_new();

    // Hoja 1: Resumen
    const summaryRows = [
      ["Reporte de Asistencia de Estudiantes"],
      [],
      ["Grado", gradeName || ""],
      ["Sección", sectionName || ""],
      ["Desde", startDate ? new Date(startDate + 'T12:00:00').toLocaleDateString("es-PE") : ""],
      ["Hasta", endDate ? new Date(endDate + 'T12:00:00').toLocaleDateString("es-PE") : ""],
      [],
      ["Resumen General"],
      ["Total registros", report.summary.total_records || 0],
      ["Asistencias", report.summary.present || 0],
      ["Tardanzas", report.summary.late || 0],
      ["Inasistencias", report.summary.absent || 0],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryRows);
    wsSummary["!cols"] = [{ wch: 22 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, "Resumen");

    // Hoja 2: Detalle por estudiante
    const detailHeader = ["Estudiante", "Días", "Asistencias", "Tardanzas", "Inasistencias", "% Asistencia"];
    const detailRows = (report.report || []).map(item => [
      item.student_name,
      item.total_days,
      item.present,
      item.late,
      item.absent,
      typeof item.attendance_rate === "number" ? item.attendance_rate / 100 : item.attendance_rate,
    ]);
    const wsDetail = XLSX.utils.aoa_to_sheet([detailHeader, ...detailRows]);
    wsDetail["!cols"] = [
      { wch: 38 }, { wch: 8 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
    ];
    // Format the % column as percentage
    const range = XLSX.utils.decode_range(wsDetail["!ref"]);
    for (let R = 1; R <= range.e.r; ++R) {
      const cellRef = XLSX.utils.encode_cell({ r: R, c: 5 });
      const cell = wsDetail[cellRef];
      if (cell && typeof cell.v === "number") {
        cell.t = "n";
        cell.z = "0%";
      }
    }
    XLSX.utils.book_append_sheet(wb, wsDetail, "Detalle");

    const fileName = `reporte_asistencia_${(gradeName || "").replace(/\s+/g, '_')}_${sectionName || ""}_${startDate}_${endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="space-y-6" data-testid="attendance-reports-tab">
      {/* Subtabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit" data-testid="reports-subtabs">
        <button
          type="button"
          onClick={() => setReportsSubtab("reporte")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            reportsSubtab === "reporte" ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="reports-subtab-reporte"
        >
          <FileText className="w-4 h-4" /> Reporte
        </button>
        <button
          type="button"
          onClick={() => setReportsSubtab("planilla")}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
            reportsSubtab === "planilla" ? "bg-white text-violet-700 shadow-sm" : "text-slate-500 hover:text-slate-700"
          }`}
          data-testid="reports-subtab-planilla"
        >
          <Download className="w-4 h-4" /> Planilla Mensual
        </button>
      </div>

      {/* Subtab: Planilla Mensual */}
      {reportsSubtab === "planilla" && (
        <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="monthly-sheet-section">
          <h3 className="text-lg font-bold text-slate-800 mb-1 flex items-center gap-2">
            <Download className="w-5 h-5 text-violet-600" />
            Planilla Mensual
          </h3>
          <p className="text-xs text-slate-500 mb-5">
            Descarga el registro de asistencia de un mes completo en formato PDF (horizontal).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Grado</label>
              <select
                value={monthlyGrade}
                onChange={(e) => setMonthlyGrade(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="monthly-grade-select"
              >
                <option value="">Seleccionar grado...</option>
                {grades.map(g => (<option key={g.id} value={g.id}>{g.nombre}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Sección</label>
              <select
                value={monthlySection}
                onChange={(e) => setMonthlySection(e.target.value)}
                disabled={!monthlyGrade}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-50 disabled:text-slate-400"
                data-testid="monthly-section-select"
              >
                <option value="">Seleccionar sección...</option>
                {monthlySections.map(s => (<option key={s.id} value={s.id}>{s.nombre}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Mes</label>
              <select
                value={monthlyMonth}
                onChange={(e) => setMonthlyMonth(parseInt(e.target.value, 10))}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="monthly-month-select"
              >
                {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Año</label>
              <select
                value={monthlyYear}
                onChange={(e) => setMonthlyYear(parseInt(e.target.value, 10))}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="monthly-year-select"
              >
                {[monthlyYear - 1, monthlyYear, monthlyYear + 1].map(y => (<option key={y} value={y}>{y}</option>))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={downloadMonthlySheet}
                disabled={downloadingMonthly || !monthlyGrade || !monthlySection}
                className="w-full px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-semibold hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="monthly-download-btn"
              >
                {downloadingMonthly ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                Descargar
              </button>
            </div>
          </div>
          {(!monthlyGrade || !monthlySection) && (
            <p className="text-xs text-amber-600 mt-3">Selecciona Grado y Sección para habilitar la descarga.</p>
          )}
        </div>
      )}

      {/* Subtab: Reporte (existente) */}
      {reportsSubtab === "reporte" && (<>
      {/* Toggle Por día / Por rango */}
      <div className="flex items-center gap-2 bg-white rounded-2xl shadow-sm p-2 w-fit mb-2">
        <button onClick={() => setStudentMode("day")} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${studentMode === "day" ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="student-mode-day">Por día</button>
        <button onClick={() => setStudentMode("range")} className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${studentMode === "range" ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid="student-mode-range">Por rango</button>
      </div>

      {studentMode === "day" && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Filter className="w-5 h-5 text-violet-600" />Asistencia diaria de estudiantes</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Grado</label>
              <select value={selectedGrade} onChange={(e) => setSelectedGrade(e.target.value)} disabled={loadingGrades} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="student-day-grade">
                <option value="">Seleccionar grado...</option>
                {grades.map(g => <option key={g.id} value={g.id}>{g.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Sección</label>
              <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} disabled={!selectedGrade} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="student-day-section">
                <option value="">Seleccionar sección...</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Fecha</label>
              <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500" data-testid="student-day-date" />
            </div>
            <div className="flex items-end">
              <button onClick={loadDayReport} disabled={loading || !selectedGrade || !selectedSection} className="w-full px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-semibold hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="student-day-generate">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />} Ver día
              </button>
            </div>
          </div>
        </div>
      )}

      {studentMode === "day" && dayReport && (
        <PremiumDailyAttendance data={dayReport} nameHeader="Alumno" okLabel="Presentes" title="Asistencia de Alumnos" />
      )}

      {studentMode === "range" && (<>
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-violet-600" />
          Filtros del Reporte de Estudiantes
        </h3>
        
        <div className="grid md:grid-cols-5 gap-4">
          {/* Grade */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Grado</label>
            <select
              data-testid="report-grade-select"
              value={selectedGrade}
              onChange={(e) => setSelectedGrade(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              disabled={loadingGrades}
            >
              <option value="">Seleccionar grado...</option>
              {grades.map(g => (
                <option key={g.id} value={g.id}>{g.nombre}</option>
              ))}
            </select>
          </div>

          {/* Section */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Sección</label>
            <select
              data-testid="report-section-select"
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
              disabled={!selectedGrade}
            >
              <option value="">Seleccionar sección...</option>
              {sections.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Desde</label>
            <input
              type="date"
              data-testid="report-start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Hasta</label>
            <input
              type="date"
              data-testid="report-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          {/* Generate button */}
          <div className="flex items-end">
            <button
              data-testid="report-generate-btn"
              onClick={loadReport}
              disabled={loading || !selectedGrade || !selectedSection}
              className="w-full px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-xl font-semibold hover:from-violet-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Generar Reporte
            </button>
          </div>
        </div>
      </div>

      {/* Report results */}
      {report && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="attendance-report-results">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4 text-white">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h3 className="text-xl font-bold">Reporte de Asistencia de Estudiantes</h3>
                <p className="text-violet-100">
                  {gradeName} - Sección {sectionName} | {new Date(startDate + 'T12:00:00').toLocaleDateString("es-PE")} - {new Date(endDate + 'T12:00:00').toLocaleDateString("es-PE")}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={exportToPDF}
                  data-testid="report-export-pdf-btn"
                  className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg flex items-center gap-2 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  Exportar PDF
                </button>
                <button
                  onClick={exportToExcel}
                  data-testid="report-export-excel-btn"
                  className="px-4 py-2 bg-emerald-500/90 hover:bg-emerald-500 text-white rounded-lg flex items-center gap-2 transition-colors font-semibold"
                >
                  <Download className="w-5 h-5" />
                  Exportar Excel
                </button>
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="p-6 border-b border-slate-200">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-slate-800">{report.summary.total_records}</p>
                <p className="text-sm text-slate-500">Total registros</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-emerald-600">{report.summary.present}</p>
                <p className="text-sm text-emerald-700">Asistencias</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{report.summary.late}</p>
                <p className="text-sm text-amber-700">Tardanzas</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{report.summary.absent}</p>
                <p className="text-sm text-red-700">Inasistencias</p>
              </div>
            </div>
          </div>

          {/* Student breakdown */}
          {report.report.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-6 py-4 text-left text-sm font-bold text-slate-700">Estudiante</th>
                    <th className="px-4 py-4 text-center text-sm font-bold text-slate-700">Días</th>
                    <th className="px-4 py-4 text-center text-sm font-bold text-emerald-600">Asistencias</th>
                    <th className="px-4 py-4 text-center text-sm font-bold text-amber-600">Tardanzas</th>
                    <th className="px-4 py-4 text-center text-sm font-bold text-red-600">Inasistencias</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-slate-700">% Asistencia</th>
                    <th className="px-4 py-4 text-center text-sm font-bold text-slate-700">Detalle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.report.map((item, idx) => (
                    <tr key={item.student_id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {item.student_photo ? (
                            <img src={item.student_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white font-bold">
                              {item.student_name?.charAt(0) || "E"}
                            </div>
                          )}
                          <span className="font-semibold text-slate-800">{item.student_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center text-slate-600">{item.total_days}</td>
                      <td className="px-4 py-4 text-center">
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                          {item.present}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                          {item.late}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                          {item.absent}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                item.attendance_rate >= 90 ? "bg-emerald-500" :
                                item.attendance_rate >= 75 ? "bg-amber-500" : "bg-red-500"
                              }`}
                              style={{ width: `${item.attendance_rate}%` }}
                            />
                          </div>
                          <span className={`font-bold ${
                            item.attendance_rate >= 90 ? "text-emerald-600" :
                            item.attendance_rate >= 75 ? "text-amber-600" : "text-red-600"
                          }`}>
                            {item.attendance_rate}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <button
                          data-testid={`view-detail-${item.student_id}`}
                          onClick={() => openDetailModal(item.student_id, item.student_name, item.student_photo)}
                          className="w-9 h-9 rounded-lg bg-violet-100 hover:bg-violet-200 flex items-center justify-center transition-colors mx-auto"
                          title="Ver detalle mensual"
                        >
                          <Eye className="w-4 h-4 text-violet-600" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-bold text-slate-700 mb-2">Sin datos de estudiantes</h3>
              <p className="text-slate-500">No hay registros de asistencia en el rango seleccionado para esta sección.</p>
            </div>
          )}
        </div>
      )}

      {/* Initial state */}
      {!report && !loading && (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-bold text-slate-700 mb-2">Reporte de Asistencia de Estudiantes</h3>
          <p className="text-slate-500 mb-4">Selecciona un grado, sección y rango de fechas para generar el reporte.</p>
          <p className="text-sm text-slate-400">El reporte mostrará las asistencias, tardanzas e inasistencias de cada alumno.</p>
        </div>
      )}
      </>)}
      </>)}

      {/* Student Detail Drawer (Side Panel) */}
      {detailModal && (
        <div className="fixed inset-0 z-[200] flex justify-end" data-testid="student-detail-drawer">
          <div className="absolute inset-0 bg-black/40 transition-opacity" onClick={() => setDetailModal(null)} />
          <div className="relative w-full max-w-3xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right" style={{ animation: "slideInRight 0.3s ease-out" }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div className="flex items-center gap-3">
                {detailModal.studentPhoto ? (
                  <img src={detailModal.studentPhoto} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white/50" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">{detailModal.studentName?.charAt(0)}</div>
                )}
                <div className="text-white">
                  <h3 className="font-bold text-lg leading-tight">Asistencia de {detailModal.studentName}</h3>
                  <p className="text-violet-200 text-sm">Detalle mensual</p>
                </div>
              </div>
              <button onClick={() => setDetailModal(null)} className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white"><X className="w-4 h-4" /></button>
            </div>

            {/* Action bar */}
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-end">
              <button
                onClick={exportIndividualPDF}
                disabled={detailLoading}
                data-testid="export-individual-pdf-btn"
                className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg flex items-center gap-2 transition-colors text-sm font-semibold disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                Reporte individual PDF
              </button>
            </div>

            {/* Month Navigation */}
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <button onClick={() => changeDetailMonth(-1)} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><ChevronLeft className="w-5 h-5 text-slate-600" /></button>
              <h4 className="text-lg font-semibold text-slate-800 capitalize">{detailMonth.toLocaleDateString("es-PE", { month: "long", year: "numeric" })}</h4>
              <button onClick={() => changeDetailMonth(1)} disabled={detailMonth.getMonth() === new Date().getMonth() && detailMonth.getFullYear() === new Date().getFullYear()} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-50 flex items-center justify-center"><ChevronRight className="w-5 h-5 text-slate-600" /></button>
            </div>

            {/* Stats */}
            {(() => {
              const st = { present: 0, absent: 0, late: 0, justified: 0, total: detailRecords.length };
              detailRecords.forEach(a => { if (st[a.status] !== undefined) st[a.status]++; });
              const pct = st.total > 0 ? Math.round(((st.present + st.justified) / st.total) * 100) : 0;
              return (
                <div className="px-5 py-3 grid grid-cols-5 gap-2">
                  <div className="bg-emerald-500 rounded-lg p-2 text-white text-center"><p className="text-[10px] text-white/80">Asistencia</p><p className="text-lg font-bold">{pct}%</p></div>
                  <div className="bg-emerald-50 rounded-lg p-2 text-center"><p className="text-[10px] text-emerald-600">Asistencias</p><p className="text-lg font-bold text-emerald-700">{st.present}</p></div>
                  <div className="bg-amber-50 rounded-lg p-2 text-center"><p className="text-[10px] text-amber-600">Tardanzas</p><p className="text-lg font-bold text-amber-700">{st.late}</p></div>
                  <div className="bg-red-50 rounded-lg p-2 text-center"><p className="text-[10px] text-red-600">Faltas</p><p className="text-lg font-bold text-red-700">{st.absent}</p></div>
                  <div className="bg-blue-50 rounded-lg p-2 text-center"><p className="text-[10px] text-blue-600">Justificadas</p><p className="text-lg font-bold text-blue-700">{st.justified}</p></div>
                </div>
              );
            })()}

            {/* Calendar */}
            <div className="px-5 py-4">
              {detailLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-violet-500 animate-spin" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"].map(d => (
                      <div key={d} className="text-center text-xs font-medium text-slate-500 py-1">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-2">
                    {(() => {
                      const y = detailMonth.getFullYear(), mo = detailMonth.getMonth();
                      const firstDay = new Date(y, mo, 1).getDay();
                      const daysInMonth = new Date(y, mo + 1, 0).getDate();
                      const cells = [];
                      for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
                      for (let day = 1; day <= daysInMonth; day++) {
                        const dateStr = `${y}-${String(mo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                        const rec = detailRecords.find(a => a.date === dateStr);
                        const isWeekend = [0, 6].includes(new Date(y, mo, day).getDay());
                        const entryTime = rec?.entry_time ? (() => { try { return new Date(rec.entry_time).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" }); } catch { return null; } })() : null;
                        const exitTime = rec?.exit_time ? (() => { try { return new Date(rec.exit_time).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit" }); } catch { return null; } })() : null;

                        if (!rec?.status) {
                          cells.push(<div key={day} className={`h-20 rounded-lg flex items-center justify-center text-sm ${isWeekend ? "bg-slate-50 text-slate-400" : "bg-slate-50 text-slate-600"}`}><span className="font-medium">{day}</span></div>);
                        } else if (rec.status === "absent" || rec.status === "justified") {
                          const cfg = rec.status === "absent" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700";
                          cells.push(
                            <div key={day} className={`h-20 rounded-lg flex flex-col items-center justify-center ${cfg}`}>
                              <span className="font-bold text-sm">{day}</span>
                              {rec.status === "absent" ? <XCircle className="w-3.5 h-3.5 mt-0.5" /> : <FileText className="w-3.5 h-3.5 mt-0.5" />}
                            </div>
                          );
                        } else {
                          const isLate = rec.status === "late";
                          cells.push(
                            <div key={day} className="h-20 rounded-lg overflow-hidden flex flex-col">
                              <div className={`flex-1 flex flex-col items-center justify-center ${entryTime ? "bg-emerald-100" : isLate ? "bg-amber-100" : "bg-emerald-100"}`}>
                                <span className={`font-bold text-sm ${isLate && !entryTime ? "text-amber-700" : "text-emerald-700"}`}>{day}</span>
                                {entryTime ? (
                                  <span className="text-emerald-700 font-medium text-center text-[10px] leading-tight">E {entryTime}</span>
                                ) : isLate ? <Clock className="w-3 h-3 text-amber-600" /> : <CheckCircle className="w-3 h-3 text-emerald-600" />}
                              </div>
                              <div className={`flex-1 flex items-center justify-center ${exitTime ? "bg-blue-100" : entryTime ? "bg-emerald-50" : isLate ? "bg-amber-50" : "bg-emerald-50"}`}>
                                {exitTime && <span className="text-blue-700 font-medium text-center text-[10px] leading-tight">S {exitTime}</span>}
                              </div>
                            </div>
                          );
                        }
                      }
                      return cells;
                    })()}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-slate-100">
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-200" /><span className="text-slate-600">Entrada</span></div>
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-200" /><span className="text-slate-600">Salida</span></div>
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-200" /><span className="text-slate-600">Tardanza</span></div>
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-3 h-3 rounded bg-red-100 border border-red-200" /><span className="text-slate-600">Falta</span></div>
                    <div className="flex items-center gap-1.5 text-xs"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-200" /><span className="text-slate-600">Justificado</span></div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// TEACHER REPORTS TAB
// ══════════════════════════════════════════════════════════════════════════════
function TeacherReportsTab({ token }) {
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [report, setReport] = useState(null);
  const [dayReport, setDayReport] = useState(null);
  const [loading, setLoading] = useState(false);
  // Modo de consulta rápido: por día / por mes / rango personalizado
  const [mode, setMode] = useState("day");
  const [dayDate, setDayDate] = useState(new Date().toISOString().split("T")[0]);
  const _now = new Date();
  const [monthSel, setMonthSel] = useState(_now.getMonth() + 1);
  const [yearSel, setYearSel] = useState(_now.getFullYear());
  const MONTHS_ES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  const headers = { Authorization: `Bearer ${token}` };

  const loadReport = async (sd = startDate, ed = endDate) => {
    setStartDate(sd); setEndDate(ed);
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: sd, end_date: ed });
      const res = await axios.get(`${API}/attendance/reports/teachers?${params}`, { headers });
      setReport(res.data);
    } catch (err) {
      console.error("Error loading teacher report:", err);
    } finally {
      setLoading(false);
    }
  };

  const runDay = (d) => {
    setDayDate(d);
    loadDayReport(d);
  };

  const loadDayReport = async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/asistencia/profesores`, { headers, params: { fecha: d } });
      setDayReport(res.data);
    } catch (err) {
      console.error("Error loading daily teacher report:", err);
      setDayReport(null);
    } finally {
      setLoading(false);
    }
  };

  // Carga automática del día actual al montar (modo "day" por defecto)
  useEffect(() => {
    loadDayReport(dayDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runMonth = (m, y) => {
    const last = new Date(y, m, 0).getDate();
    loadReport(`${y}-${String(m).padStart(2,"0")}-01`, `${y}-${String(m).padStart(2,"0")}-${String(last).padStart(2,"0")}`);
  };

  const exportToPDF = () => {
    if (!report) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.setTextColor(88, 28, 135);
    doc.text("Reporte de Asistencia de Profesores", pageWidth / 2, 20, { align: "center" });

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const dateRange = `Período: ${new Date(startDate + 'T12:00:00').toLocaleDateString("es-PE")} - ${new Date(endDate + 'T12:00:00').toLocaleDateString("es-PE")}`;
    doc.text(dateRange, pageWidth / 2, 28, { align: "center" });

    if (report.report.length > 0) {
      const tableData = report.report.map(item => [
        item.teacher_name,
        item.total_days,
        item.present,
        item.late,
        item.absent,
        item.justified,
        `${item.attendance_rate}%`
      ]);

      autoTable(doc, {
        startY: 40,
        head: [['Profesor', 'Días', 'Presente', 'Tardanza', 'Ausente', 'Justificado', '% Asist.']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontSize: 10, fontStyle: 'bold' },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 18, halign: 'center' },
          2: { cellWidth: 22, halign: 'center' },
          3: { cellWidth: 22, halign: 'center' },
          4: { cellWidth: 22, halign: 'center' },
          5: { cellWidth: 26, halign: 'center' },
          6: { cellWidth: 22, halign: 'center' }
        },
        alternateRowStyles: { fillColor: [248, 250, 252] }
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(`Generado el ${new Date().toLocaleDateString("es-PE")} - Página ${i} de ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: "center" });
    }

    doc.save(`reporte_asistencia_profesores_${startDate}_${endDate}.pdf`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: "bg-emerald-100 text-emerald-700",
      late: "bg-amber-100 text-amber-700",
      absent: "bg-red-100 text-red-700",
      justified: "bg-blue-100 text-blue-700"
    };
    const labels = { present: "Presente", late: "Tardanza", absent: "Ausente", justified: "Justificado" };
    return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || "bg-slate-100 text-slate-500"}`}>{labels[status] || status}</span>;
  };

  return (
    <div className="space-y-6" data-testid="teacher-reports-tab">
      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <Filter className="w-5 h-5 text-violet-600" />
            Reporte de Asistencia de Profesores
          </h3>
          {/* Segmented toggle */}
          <div className="inline-flex p-1 bg-slate-100 rounded-2xl shadow-inner" data-testid="teacher-report-mode">
            {[
              { id: "day", label: "Por día", icon: Calendar },
              { id: "month", label: "Por mes", icon: CalendarDays },
              { id: "custom", label: "Personalizado", icon: Filter },
            ].map((m) => {
              const Icon = m.icon;
              const active = mode === m.id;
              return (
                <button key={m.id} onClick={() => setMode(m.id)} data-testid={`teacher-report-mode-${m.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${active ? "bg-white text-violet-600 shadow-md" : "text-slate-500 hover:text-slate-700"}`}>
                  <Icon className="w-4 h-4" />
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        {mode === "day" && (
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Selecciona el día</label>
              <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)}
                className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="teacher-report-day-date" />
            </div>
            <button onClick={() => runDay(dayDate)} disabled={loading}
              className="px-6 py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="teacher-report-day-generate">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Ver día
            </button>
            <button onClick={() => runDay(new Date().toISOString().split("T")[0])}
              className="px-4 py-3 bg-slate-100 text-slate-600 rounded-xl font-medium hover:bg-slate-200 transition-colors">
              Hoy
            </button>
          </div>
        )}

        {mode === "month" && (
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Mes</label>
              <select value={monthSel} onChange={(e) => setMonthSel(Number(e.target.value))}
                className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 min-w-[150px]"
                data-testid="teacher-report-month-select">
                {MONTHS_ES.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Año</label>
              <select value={yearSel} onChange={(e) => setYearSel(Number(e.target.value))}
                className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="teacher-report-year-select">
                {[_now.getFullYear(), _now.getFullYear() - 1, _now.getFullYear() - 2].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button onClick={() => runMonth(monthSel, yearSel)} disabled={loading}
              className="px-6 py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="teacher-report-month-generate">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Ver mes
            </button>
          </div>
        )}

        {mode === "custom" && (
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Desde</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="teacher-report-start-date" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Hasta</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500"
                data-testid="teacher-report-end-date" />
            </div>
            <div className="flex items-end">
              <button onClick={() => loadReport(startDate, endDate)} disabled={loading}
                className="w-full px-6 py-3 bg-violet-600 text-white rounded-xl font-semibold hover:bg-violet-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="teacher-report-generate">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                Generar Reporte
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Vista DÍA premium (fusión): Horario / Entrada / Salida / Horas / Estado */}
      {mode === "day" && dayReport && (
        <PremiumDailyAttendance data={dayReport} nameHeader="Profesor" okLabel="Completos" title="Asistencia de Profesores" />
      )}

      {/* Report results */}
      {mode !== "day" && report && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="teacher-report-summary">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 text-center">
              <p className="text-2xl font-bold text-slate-800">{report.summary.total_records}</p>
              <p className="text-xs text-slate-500 font-medium">Total registros</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-4 shadow-sm border border-emerald-200 text-center">
              <p className="text-2xl font-bold text-emerald-700">{report.summary.present}</p>
              <p className="text-xs text-emerald-600 font-medium">Presentes</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-4 shadow-sm border border-amber-200 text-center">
              <p className="text-2xl font-bold text-amber-700">{report.summary.late}</p>
              <p className="text-xs text-amber-600 font-medium">Tardanzas</p>
            </div>
            <div className="bg-red-50 rounded-2xl p-4 shadow-sm border border-red-200 text-center">
              <p className="text-2xl font-bold text-red-700">{report.summary.absent}</p>
              <p className="text-xs text-red-600 font-medium">Ausentes</p>
            </div>
            <div className="bg-blue-50 rounded-2xl p-4 shadow-sm border border-blue-200 text-center">
              <p className="text-2xl font-bold text-blue-700">{report.summary.justified}</p>
              <p className="text-xs text-blue-600 font-medium">Justificados</p>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-violet-600" />
                Detalle por Profesor ({report.report.length})
              </h3>
              <button onClick={exportToPDF}
                className="px-4 py-2 bg-violet-100 text-violet-700 rounded-xl text-sm font-semibold hover:bg-violet-200 transition-colors flex items-center gap-2"
                data-testid="teacher-report-export-pdf">
                <Download className="w-4 h-4" /> Exportar PDF
              </button>
            </div>

            {report.report.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No hay registros de asistencia en este período</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Profesor</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase">Días</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-emerald-600 uppercase">Presente</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-amber-600 uppercase">Tardanza</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-red-600 uppercase">Ausente</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-blue-600 uppercase">Justificado</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase">% Asist.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.report.map((t) => (
                      <tr key={t.teacher_id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            {t.teacher_photo ? (
                              <img src={t.teacher_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                                {t.teacher_name?.charAt(0) || "P"}
                              </div>
                            )}
                            <span className="font-medium text-slate-800 text-sm">{t.teacher_name}</span>
                          </div>
                        </td>
                        <td className="text-center px-3 py-3 text-sm font-medium text-slate-600">{t.total_days}</td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-emerald-600">{t.present}</span></td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-amber-600">{t.late}</span></td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-red-600">{t.absent}</span></td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-blue-600">{t.justified}</span></td>
                        <td className="text-center px-3 py-3">
                          <span className={`text-sm font-bold ${t.attendance_rate >= 90 ? "text-emerald-600" : t.attendance_rate >= 70 ? "text-amber-600" : "text-red-600"}`}>
                            {t.attendance_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Recent records */}
          {report.records && report.records.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-bold text-slate-800">Últimos Registros</h3>
              </div>
              <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {report.records.slice(0, 20).map((r, idx) => {
                  const teacher = report.report.find(t => t.teacher_id === r.user_id);
                  return (
                    <div key={idx} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-50">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                        {teacher?.teacher_name?.charAt(0) || "P"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm truncate">{teacher?.teacher_name || r.user_id}</p>
                        <p className="text-xs text-slate-400">{r.date} — {r.check_in_time || r.entry_time || ""}</p>
                      </div>
                      {getStatusBadge(r.status)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// MAINTENANCE REPORTS TAB
// ══════════════════════════════════════════════════════════════════════════════
function MaintenanceReportsTab({ token }) {
  const [mode, setMode] = useState("day"); // "day" | "range"
  const [dayDate, setDayDate] = useState(new Date().toISOString().split("T")[0]);
  const [dayReport, setDayReport] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadDayReport(dayDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDayReport = async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/asistencia/administrativo`, { headers, params: { fecha: d } });
      setDayReport(res.data);
    } catch (err) {
      console.error("Error loading admin daily report:", err);
      setDayReport(null);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      const res = await axios.get(`${API}/attendance/reports/maintenance?${params}`, { headers });
      setReport(res.data);
    } catch (err) {
      console.error("Error loading maintenance report:", err);
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!report) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.setTextColor(180, 83, 9); // amber-700
    doc.text("Reporte de Asistencia — Personal Administrativo", pageWidth / 2, 20, { align: "center" });

    doc.setFontSize(11);
    doc.setTextColor(100, 100, 100);
    const dateRange = `Período: ${new Date(startDate + 'T12:00:00').toLocaleDateString("es-PE")} - ${new Date(endDate + 'T12:00:00').toLocaleDateString("es-PE")}`;
    doc.text(dateRange, pageWidth / 2, 28, { align: "center" });

    if (report.report.length > 0) {
      const tableData = report.report.map(item => [
        item.full_name,
        item.role_label || "—",
        item.total_days,
        item.present,
        item.late,
        item.absent,
        item.justified,
        `${item.attendance_rate}%`,
      ]);

      autoTable(doc, {
        startY: 40,
        head: [['Personal', 'Rol', 'Días', 'Presente', 'Tardanza', 'Ausente', 'Justificado', '% Asist.']],
        body: tableData,
        theme: 'striped',
        headStyles: { fillColor: [245, 158, 11], textColor: 255, fontSize: 10, fontStyle: 'bold' },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 42 },
          1: { cellWidth: 28 },
          2: { cellWidth: 15, halign: 'center' },
          3: { cellWidth: 20, halign: 'center' },
          4: { cellWidth: 20, halign: 'center' },
          5: { cellWidth: 20, halign: 'center' },
          6: { cellWidth: 22, halign: 'center' },
          7: { cellWidth: 18, halign: 'center' },
        },
        alternateRowStyles: { fillColor: [254, 252, 232] },
      });
    }

    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.text(
        `Generado el ${new Date().toLocaleDateString("es-PE")} - Página ${i} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save(`reporte_asistencia_administrativos_${startDate}_${endDate}.pdf`);
  };

  const getStatusBadge = (status) => {
    const styles = {
      present: "bg-emerald-100 text-emerald-700",
      late: "bg-amber-100 text-amber-700",
      absent: "bg-red-100 text-red-700",
      justified: "bg-blue-100 text-blue-700",
    };
    const labels = { present: "Presente", late: "Tardanza", absent: "Ausente", justified: "Justificado" };
    return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${styles[status] || "bg-slate-100 text-slate-500"}`}>{labels[status] || status}</span>;
  };

  return (
    <div className="space-y-6" data-testid="maintenance-reports-tab">
      {/* Toggle Por día / Por rango */}
      <div className="flex items-center gap-2 bg-white rounded-2xl shadow-sm p-2 w-fit">
        <button onClick={() => setMode("day")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${mode === "day" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="admin-mode-day">Por día</button>
        <button onClick={() => setMode("range")}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${mode === "range" ? "bg-amber-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid="admin-mode-range">Por rango</button>
      </div>

      {mode === "day" && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Fecha</label>
              <input type="date" value={dayDate} onChange={(e) => setDayDate(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="admin-day-date" />
            </div>
            <button onClick={() => loadDayReport(dayDate)} disabled={loading}
              className="px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              data-testid="admin-day-generate">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />} Ver día
            </button>
            <button onClick={() => { const t = new Date().toISOString().split("T")[0]; setDayDate(t); loadDayReport(t); }}
              className="px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition-colors"
              data-testid="admin-day-today">Hoy</button>
          </div>
        </div>
      )}

      {mode === "day" && dayReport && (
        <PremiumDailyAttendance data={dayReport} nameHeader="Personal" okLabel="Presentes" title="Asistencia Personal Administrativo" pdfColor={[245, 158, 11]} />
      )}

      {mode === "range" && (
      <>
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Filter className="w-5 h-5 text-amber-600" />
          Reporte de Asistencia — Personal Administrativo
        </h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Desde</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="maintenance-report-start-date" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Hasta</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="maintenance-report-end-date" />
          </div>
          <div className="flex items-end">
            <button onClick={loadReport} disabled={loading}
              className="w-full px-6 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="maintenance-report-generate">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Generar Reporte
            </button>
          </div>
        </div>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="maintenance-report-summary">
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 text-center">
              <p className="text-2xl font-bold text-slate-800">{report.summary.total_records}</p>
              <p className="text-xs text-slate-500 font-medium">Total registros</p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-4 shadow-sm border border-emerald-200 text-center">
              <p className="text-2xl font-bold text-emerald-700">{report.summary.present}</p>
              <p className="text-xs text-emerald-600 font-medium">Presentes</p>
            </div>
            <div className="bg-amber-50 rounded-2xl p-4 shadow-sm border border-amber-200 text-center">
              <p className="text-2xl font-bold text-amber-700">{report.summary.late}</p>
              <p className="text-xs text-amber-600 font-medium">Tardanzas</p>
            </div>
            <div className="bg-red-50 rounded-2xl p-4 shadow-sm border border-red-200 text-center">
              <p className="text-2xl font-bold text-red-700">{report.summary.absent}</p>
              <p className="text-xs text-red-600 font-medium">Ausentes</p>
            </div>
            <div className="bg-blue-50 rounded-2xl p-4 shadow-sm border border-blue-200 text-center">
              <p className="text-2xl font-bold text-blue-700">{report.summary.justified}</p>
              <p className="text-xs text-blue-600 font-medium">Justificados</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-600" />
                Detalle por Personal ({report.report.length})
              </h3>
              <button onClick={exportToPDF}
                className="px-4 py-2 bg-amber-100 text-amber-700 rounded-xl text-sm font-semibold hover:bg-amber-200 transition-colors flex items-center gap-2"
                data-testid="maintenance-report-export-pdf">
                <Download className="w-4 h-4" /> Exportar PDF
              </button>
            </div>

            {report.report.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No hay registros de asistencia en este período</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-6 py-3 text-xs font-bold text-slate-500 uppercase">Personal</th>
                      <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase">Rol</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase">Días</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-emerald-600 uppercase">Presente</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-amber-600 uppercase">Tardanza</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-red-600 uppercase">Ausente</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-blue-600 uppercase">Justificado</th>
                      <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase">% Asist.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.report.map((p) => (
                      <tr key={p.user_id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-3">
                            {p.photo_url ? (
                              <img src={p.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-xs">
                                {p.full_name?.charAt(0) || "M"}
                              </div>
                            )}
                            <span className="font-medium text-slate-800 text-sm">{p.full_name}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-600">{p.role_label || "—"}</td>
                        <td className="text-center px-3 py-3 text-sm font-medium text-slate-600">{p.total_days}</td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-emerald-600">{p.present}</span></td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-amber-600">{p.late}</span></td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-red-600">{p.absent}</span></td>
                        <td className="text-center px-3 py-3"><span className="text-sm font-bold text-blue-600">{p.justified}</span></td>
                        <td className="text-center px-3 py-3">
                          <span className={`text-sm font-bold ${p.attendance_rate >= 90 ? "text-emerald-600" : p.attendance_rate >= 70 ? "text-amber-600" : "text-red-600"}`}>
                            {p.attendance_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {report.records && report.records.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200">
                <h3 className="font-bold text-slate-800">Últimos Registros</h3>
              </div>
              <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
                {report.records.slice(0, 20).map((r, idx) => {
                  const person = report.report.find(p => p.user_id === r.user_id);
                  return (
                    <div key={idx} className="px-6 py-3 flex items-center gap-4 hover:bg-slate-50">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-xs">
                        {person?.full_name?.charAt(0) || "M"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 text-sm truncate">{person?.full_name || r.user_id}</p>
                        <p className="text-xs text-slate-400">{r.date}{person?.role_label ? ` · ${person.role_label}` : ""}</p>
                      </div>
                      {getStatusBadge(r.status)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════
export default function AttendancePage({ user, token, subdomain, onLogout, initialView }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState(initialView || searchParams.get("tab") || "home");
  const [scanContext, setScanContext] = useState(null);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) setActiveView(tabParam);
  }, [searchParams]);
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axios.get(`${API}/school/info`, { headers });
      setSettings(res.data);
    } catch (err) {
      console.error("Error loading settings:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-10 h-10 text-[#001f4b] animate-spin" />
      </div>
    );
  }

  const isMobileQRMode = activeView === "qr-scanner";
  const isSubView = activeView !== "home";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="attendance-page">
      <Sidebar 
        active="asistencias"
        onNavigate={() => {}}
        expanded={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        onLogout={onLogout}
        schoolName={settings?.school_name}
        subdomain={subdomain}
        user={user}
      />
      
      <div className="flex-1 flex flex-col min-w-0">
        <DashboardHeader
          user={user}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
          onLogout={onLogout}
          logoUrl={settings?.logo_url}
          schoolName={settings?.school_name}
          subdomain={subdomain}
        />

        {/* Main Content */}
        <main className="flex-1 p-3 sm:p-6 lg:p-8 pb-20 lg:pb-8">
          {/* Page Title — hidden on mobile when QR scanner is active */}
          <div className={`${isMobileQRMode ? "hidden lg:block" : ""} relative overflow-hidden rounded-3xl mb-8`}>
            <div className="absolute inset-0 bg-gradient-to-r from-teal-600 to-emerald-600">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            </div>
            <div className="relative px-8 py-10 flex items-center gap-6">
              <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-xl">
                <ClipboardCheck className="w-10 h-10 text-teal-600" />
              </div>
              <div className="text-white">
                <h1 className="text-4xl font-bold tracking-tight mb-2">Asistencias</h1>
                <p className="text-teal-200 text-lg">Registro y control de asistencia escolar</p>
              </div>
            </div>
          </div>

          {/* Back button when in sub-view */}
          {isSubView && (
            <button
              onClick={() => setActiveView("home")}
              className={`${isMobileQRMode ? "hidden lg:flex" : "flex"} items-center gap-2 mb-6 px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-slate-600 hover:text-slate-800 hover:bg-slate-50 hover:border-slate-300 transition-all font-medium text-sm shadow-sm`}
              data-testid="back-to-home"
            >
              <ChevronLeft className="w-4 h-4" />
              Volver al panel de asistencia
            </button>
          )}

          {/* ─── HOME: Section Cards ─── */}
          {activeView === "home" && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="attendance-home">
              {/* SECTION 1: Estudiantes */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="section-students">
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Estudiantes</h2>
                      <p className="text-blue-200 text-sm">Asistencia de alumnos</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button
                    onClick={() => { setScanContext("student"); setActiveView("qr-scanner"); }}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-blue-50 border-2 border-slate-200 hover:border-blue-300 rounded-xl transition-all group"
                    data-testid="btn-students-qr"
                  >
                    <div className="w-10 h-10 rounded-lg bg-violet-100 group-hover:bg-violet-500 flex items-center justify-center transition-colors">
                      <QrCode className="w-5 h-5 text-violet-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Escanear QR</p>
                      <p className="text-xs text-slate-400">Registro automático con código QR</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
                  </button>
                  <button
                    onClick={() => setActiveView("students")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-blue-50 border-2 border-slate-200 hover:border-blue-300 rounded-xl transition-all group"
                    data-testid="btn-students-manual"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 group-hover:bg-blue-500 flex items-center justify-center transition-colors">
                      <ClipboardCheck className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Marcar Manual</p>
                      <p className="text-xs text-slate-400">Lista de alumnos con botones de estado</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
                  </button>
                </div>
              </div>

              {/* SECTION 2: Profesores */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="section-teachers">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Profesores</h2>
                      <p className="text-indigo-200 text-sm">Asistencia de docentes</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button
                    onClick={() => { setScanContext("teacher"); setActiveView("qr-scanner"); }}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-indigo-50 border-2 border-slate-200 hover:border-indigo-300 rounded-xl transition-all group"
                    data-testid="btn-teachers-qr"
                  >
                    <div className="w-10 h-10 rounded-lg bg-violet-100 group-hover:bg-violet-500 flex items-center justify-center transition-colors">
                      <QrCode className="w-5 h-5 text-violet-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Escanear QR</p>
                      <p className="text-xs text-slate-400">Registro automático con código QR</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                  </button>
                  <button
                    onClick={() => setActiveView("teachers")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-indigo-50 border-2 border-slate-200 hover:border-indigo-300 rounded-xl transition-all group"
                    data-testid="btn-teachers-manual"
                  >
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 group-hover:bg-indigo-500 flex items-center justify-center transition-colors">
                      <ClipboardCheck className="w-5 h-5 text-indigo-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Marcar Manual</p>
                      <p className="text-xs text-slate-400">Lista de docentes con botones de estado</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-indigo-500 transition-colors" />
                  </button>
                </div>
              </div>

              {/* SECTION 3: Reportes */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="section-reports">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Reportes</h2>
                      <p className="text-amber-200 text-sm">Reportes de asistencia</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button
                    onClick={() => setActiveView("reports")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-300 rounded-xl transition-all group"
                    data-testid="btn-reports-students"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 group-hover:bg-blue-500 flex items-center justify-center transition-colors">
                      <Users className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Reportes Estudiantes</p>
                      <p className="text-xs text-slate-400">Asistencia por grado y sección</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
                  </button>
                  <button
                    onClick={() => setActiveView("reports-teachers")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-300 rounded-xl transition-all group"
                    data-testid="btn-reports-teachers"
                  >
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 group-hover:bg-indigo-500 flex items-center justify-center transition-colors">
                      <UserCheck className="w-5 h-5 text-indigo-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Reportes Profesores</p>
                      <p className="text-xs text-slate-400">Asistencia de docentes</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
                  </button>
                </div>

                {/* Status legend */}
                <div className="px-5 pb-5">
                  <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Estados</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-700 font-medium">
                      <CheckCircle2 className="w-3 h-3" /> Presente
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-amber-100 text-amber-700 font-medium">
                      <Clock className="w-3 h-3" /> Tardanza
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-red-100 text-red-700 font-medium">
                      <XCircle className="w-3 h-3" /> Ausente
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-blue-100 text-blue-700 font-medium">
                      <AlertTriangle className="w-3 h-3" /> Justificado
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-slate-100 text-slate-500 font-medium">
                      <Circle className="w-3 h-3" /> Pendiente
                    </span>
                  </div>
                </div>
              </div>

              {/* SECTION 4: Alimentación (PAE) - Solo owner/admin */}
              {(user?.role === "owner" || user?.role === "admin") && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="section-alimentacion">
                <div className="bg-gradient-to-r from-emerald-600 to-green-500 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <UtensilsCrossed className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Alimentacion</h2>
                      <p className="text-emerald-200 text-sm">Control de comedor escolar</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button
                    onClick={() => setActiveView("alimentacion")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-emerald-50 border-2 border-slate-200 hover:border-emerald-300 rounded-xl transition-all group"
                    data-testid="btn-alimentacion-registros"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 group-hover:bg-emerald-500 flex items-center justify-center transition-colors">
                      <ClipboardCheck className="w-5 h-5 text-emerald-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Registros del día</p>
                      <p className="text-xs text-slate-400">Ver asistencias de alimentacion</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-500 transition-colors" />
                  </button>
                  <button
                    disabled
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl opacity-50 cursor-not-allowed"
                    data-testid="btn-alimentacion-reportes"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-400" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-500 text-sm">Reportes Alimentacion</p>
                      <p className="text-xs text-slate-400">Proximamente</p>
                    </div>
                  </button>
                </div>
              </div>
              )}

              {/* SECTION 5: Personal Administrativo */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="section-maintenance">
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Wrench className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Personal Administrativo</h2>
                      <p className="text-amber-100 text-sm">Asistencia administrativa</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button
                    onClick={() => { setScanContext("personal_mantenimiento"); setActiveView("qr-scanner"); }}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-300 rounded-xl transition-all group"
                    data-testid="btn-maintenance-qr"
                  >
                    <div className="w-10 h-10 rounded-lg bg-violet-100 group-hover:bg-violet-500 flex items-center justify-center transition-colors">
                      <QrCode className="w-5 h-5 text-violet-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Escanear QR</p>
                      <p className="text-xs text-slate-400">Registro automático con código QR</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
                  </button>
                  <button
                    onClick={() => setActiveView("maintenance")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-300 rounded-xl transition-all group"
                    data-testid="btn-maintenance-manual"
                  >
                    <div className="w-10 h-10 rounded-lg bg-amber-100 group-hover:bg-amber-500 flex items-center justify-center transition-colors">
                      <ClipboardCheck className="w-5 h-5 text-amber-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Marcar Manual</p>
                      <p className="text-xs text-slate-400">Lista de personal con botones de estado</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
                  </button>
                  <button
                    onClick={() => setActiveView("reports-maintenance")}
                    className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-amber-50 border-2 border-slate-200 hover:border-amber-300 rounded-xl transition-all group"
                    data-testid="btn-reports-maintenance"
                  >
                    <div className="w-10 h-10 rounded-lg bg-orange-100 group-hover:bg-orange-500 flex items-center justify-center transition-colors">
                      <FileText className="w-5 h-5 text-orange-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Reportes Administrativos</p>
                      <p className="text-xs text-slate-400">Asistencia del personal administrativo</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-amber-500 transition-colors" />
                  </button>
                </div>
              </div>

              {/* SECTION 6: Movilidad - Solo owner/admin */}
              {(user?.role === "owner" || user?.role === "admin") && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-testid="section-movilidad">
                <div className="bg-gradient-to-r from-purple-600 to-violet-500 px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Bus className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Movilidad</h2>
                      <p className="text-purple-200 text-sm">Control de transporte escolar</p>
                    </div>
                  </div>
                </div>
                <div className="p-5 space-y-3">
                  <button onClick={() => setActiveView("movilidad")} className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 hover:bg-purple-50 border-2 border-slate-200 hover:border-purple-300 rounded-xl transition-all group" data-testid="btn-movilidad-registros">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 group-hover:bg-purple-500 flex items-center justify-center transition-colors">
                      <ClipboardCheck className="w-5 h-5 text-purple-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="font-semibold text-slate-800 text-sm">Registros del día</p>
                      <p className="text-xs text-slate-400">Ver registros de movilidad</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-purple-500 transition-colors" />
                  </button>
                  <button disabled className="w-full flex items-center gap-4 px-4 py-4 bg-slate-50 border-2 border-slate-200 rounded-xl opacity-50 cursor-not-allowed" data-testid="btn-movilidad-reportes">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><FileText className="w-5 h-5 text-slate-400" /></div>
                    <div className="text-left flex-1"><p className="font-semibold text-slate-500 text-sm">Reportes Movilidad</p><p className="text-xs text-slate-400">Proximamente</p></div>
                  </button>
                </div>
              </div>
              )}

            </div>
          )}

          {/* ─── SUB-VIEWS ─── */}
          {activeView === "students" && (
            <StudentAttendanceTab token={token} schoolId={user?.school_id} />
          )}
          
          {activeView === "teachers" && (
            <TeacherAttendanceTab token={token} schoolId={user?.school_id} />
          )}
          
          {activeView === "maintenance" && (
            <MaintenanceAttendanceTab token={token} />
          )}
          
          {activeView === "qr-scanner" && (
            <QRScannerTab token={token} schoolId={user?.school_id} roleFilter={scanContext} user={user} />
          )}
          
          {activeView === "reports" && (
            <ReportsTab token={token} schoolId={user?.school_id} />
          )}
          
          {activeView === "reports-teachers" && (
            <TeacherReportsTab token={token} schoolId={user?.school_id} />
          )}

          {activeView === "reports-maintenance" && (
            <MaintenanceReportsTab token={token} />
          )}

          {activeView === "alimentacion" && (
            <PaeRegistrosDia user={user} token={token} subdomain={subdomain} embedded />
          )}

          {activeView === "movilidad" && (
            <MovilidadRegistrosDia user={user} token={token} subdomain={subdomain} embedded />
          )}
        </main>
      </div>
      <MobileBottomNav role={user?.role === "admin" ? "admin" : "owner"} />
      <FloatingHelpAvatar subdomain={subdomain} />
    </div>
  );
}
